import React, { useEffect, useState, useRef } from 'react'
import isIneligibleTest from '@utils/isIneligibleTest'
import isHvacEligibilityComplete from '@utils/isHvacEligibilityComplete'
import isHeaEligibilityComplete from '@utils/isHeaEligibilityComplete'
import isHealthyHomeEligibilityComplete from '@utils/isHealthyHomeEligibilityComplete'
import urlPathSetter from '@utils/urlPathSetter'

import Footer from '@components/structural/Footer'
import FormEffect from './utils/FormEffect'
import Main from '@components/structural/Main'
import Navigation from '@components/structural/Navigation'
import FixedHelpButton from '@components/structural/FixedHelpButton'
import { useClearFollowingValues } from '@utils/clearFollowingValues'
import config from '@src/config'
import goNextStep from './utils/goNextStep'
import scrollTop from '@utils/scrollTop'
import useBeforeUnload from '@utils/useBeforeUnload'
import Modal from '@components/ui/Modal'
import { CallUsBody, CallUsFooter } from '@components/miscellaneous/CallUs'
import ErrorFocus from './components/form/ErrorFocus'
import isCapHeaCustomer from './utils/isCapHeaCustomer'
import isEversourceGasLead from './utils/isEversourceGasLead'
import isValidUsPhone from '@utils/isValidUsPhone'
import { FormProvider, useFormContext } from '@context/FormContext'
import hotjar from './utils/hotjar'
import { initClarity, clarityEvent } from './utils/clarity'
import { clarityProjectId } from '@src/constant'

initClarity(clarityProjectId)

/**
 * Recursively counts the number of actual error messages in a (potentially nested) errors object.
 * Handles paths like contactInfo.email produced by Yup's abortEarly: false mode.
 */
const countErrors = (errorObj) => {
  if (!errorObj || typeof errorObj !== 'object' || Array.isArray(errorObj)) return 0
  let count = 0
  for (const key in errorObj) {
    const value = errorObj[key]
    if (typeof value === 'string' && value.trim().length > 0) {
      count++
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      count += countErrors(value)
    }
  }
  return count
}

const AppContent = ({ initialStep = 0 }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStep)
  const [dataWasSent, setDataWasSent] = useState(false)
  const [isIneligible, setIsIneligible] = useState(false)
  const [redirectPage, setRedirectPage] = useState(false)
  const [validationSchema, setValidationSchema] = useState(null)
  const [enableSubmitButton, setEnableSubmitButton] = useState(false)
  const [blockNextStepUntilHowHeated, setBlockNextStepUntilHowHeated] = useState(false)

  const {
    values,
    errors,
    isSubmitting,
    isValidating,
    submitCount,
    setFieldValue,
    setErrors,
    setTouched,
    validateForm,
    getCurrentValues,
    setIsSubmitting,
    setSubmitCount,
  } = useFormContext()

  const steps = config.steps || []
  const CurrentStep = steps[currentStepIndex]

  // Ref keeps the step index fresh inside async submit handlers (avoids stale closures)
  const currentStepIndexRef = useRef(currentStepIndex)
  useEffect(() => {
    currentStepIndexRef.current = currentStepIndex
  }, [currentStepIndex])

  if (!CurrentStep) {
    if (process.env.NODE_ENV === 'development') {
      console.error('CurrentStep is undefined. Steps:', steps, 'Index:', currentStepIndex)
    }
    return <div>Error: Step not found at index {currentStepIndex}</div>
  }

  useBeforeUnload((e) => {
    if (process.env.NODE_ENV === 'production' && !dataWasSent) {
      e.preventDefault()
      e.returnValue = ''
      return ''
    }
  })

  useEffect(() => {
    hotjar.heatMap()
  }, [])

  useEffect(() => {
    scrollTop()
  }, [currentStepIndex, steps])

  // Reset the "howHeated" block flag whenever the user leaves the Eligibility step
  useEffect(() => {
    if (currentStepIndex !== 1) {
      setBlockNextStepUntilHowHeated(false)
    }
  }, [currentStepIndex])

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Block advancing from HVAC Sales Eligibility until "How is your home heated?" is answered.
    // Read from the ref so we always have the latest values, not a stale closure snapshot.
    const latestValues = (typeof getCurrentValues === 'function' ? getCurrentValues() : null) || values || {}
    const isHvacPath = String(latestValues.path ?? '').trim().toLowerCase() === 'hvac'
    const fuelAssistanceIsNo = latestValues.fuelAssistance === 'no'
    const familySizeStr = latestValues.familySize != null ? String(latestValues.familySize).trim() : ''
    const howHeatedQuestionVisible = fuelAssistanceIsNo && familySizeStr !== '' && ['1', '1-4'].includes(familySizeStr)
    const howHeatedIsEmpty =
      latestValues.howHeated == null ||
      latestValues.howHeated === '' ||
      (typeof latestValues.howHeated === 'string' && latestValues.howHeated.trim() === '')

    if (currentStepIndexRef.current === 1 && isHvacPath && howHeatedQuestionVisible && howHeatedIsEmpty) {
      return
    }

    try {
      setIsSubmitting(true)
      setSubmitCount((prev) => prev + 1)

      const stepIndex = currentStepIndexRef.current
      const currentStep = steps[stepIndex]
      const isEligibilityStep = currentStep?.info?.key === 'eligibility'

      // Non-eligibility steps run Yup validation before proceeding
      if (!isEligibilityStep) {
        const validationErrors = await validateForm(validationSchema)
        if (countErrors(validationErrors) > 0) {
          setIsSubmitting(false)
          setTouched(true)
          return
        }
      }

      // Fire Clarity event when user clicks Next on the ChoosePath step (step 0)
      if (stepIndex === 0) {
        const pathEventMap = {
          hea: 'no_cost_mass_save_performed',
          hvac: 'hvac_replacement_or_service_performed',
          'hvac-service': 'hvac_repair_and_maintenance_performed',
        }
        const selectedPath = latestValues.path
        clarityEvent(pathEventMap[selectedPath])
      }

      if (isEligibilityStep) {
        const ineligibleReason = isIneligibleTest(values)
        if (ineligibleReason) {
          setIsIneligible(ineligibleReason)
          setIsSubmitting(false)
          return
        }

        // Eligible — advance to ContactInfo and reset form error state
        setErrors({})
        setTouched(false)
        setSubmitCount(0)
        setCurrentStepIndex(2)
        urlPathSetter({ ...values, step: 'contactInfo' })
        setIsSubmitting(false)
        return
      }

      await goNextStep(
        values,
        { setSubmitCount, setIsSubmitting },
        currentStep,
        setDataWasSent,
        setCurrentStepIndex,
        setIsIneligible
      )
    } catch (error) {
      console.error('Error in form submission:', error)
      setIsSubmitting(false)
      alert(error.message || 'An error occurred. Please try again.')
    }
  }

  const { path = '', salesOrService = '' } = values || {}

  // Derived flags used for contact-info step detection
  const hasPath = values.path === 'hea' || values.path === 'hvac' || values.path === 'healthyhome'
  const hasEligibilityAnswers =
    values.twoYears || values.electricProvider || values.gasProvider || values.howHeated || values.paymentAssistance
  const hasContactInfo = values.contactInfo?.firstName
  const hasSchedule = values.schedule
  const isOnContactInfoStep = currentStepIndex === 2 || (hasContactInfo && !hasSchedule)

  const errorCount = countErrors(errors)

  // HEA requires the utility-bill question, and the account holder's name and phone when it is
  // "Someone Else". Yup does not gate the ContactInfo Next button, so it is enforced here too.
  const billHolderOk =
    values?.path !== 'hea' ||
    values?.contactInfo?.nameOnBill === 'mine' ||
    (values?.contactInfo?.nameOnBill === 'someoneElse' &&
      !!values?.contactInfo?.secondaryFirstName?.trim() &&
      !!values?.contactInfo?.secondaryLastName?.trim() &&
      isValidUsPhone(values?.contactInfo?.secondaryPhoneNumber))

  // ── Eligibility step: Next button stays disabled until validation passes ──
  const eligibilityValidatedAndCompleteRef = useRef(false)
  const eligibilityHasValidatedOnceRef = useRef(false)
  useEffect(() => {
    if (currentStepIndex !== 1) {
      eligibilityValidatedAndCompleteRef.current = false
      eligibilityHasValidatedOnceRef.current = false
      return
    }
    if (isValidating) {
      eligibilityHasValidatedOnceRef.current = true
      eligibilityValidatedAndCompleteRef.current = false
      return
    }
    if (errorCount > 0) {
      eligibilityValidatedAndCompleteRef.current = false
      return
    }
    if (errorCount === 0 && eligibilityHasValidatedOnceRef.current) {
      eligibilityValidatedAndCompleteRef.current = true
    }
  }, [currentStepIndex, isValidating, errorCount])

  // ── Determine whether the Next button should be disabled ──
  let hasValidationErrors = false
  if (validationSchema) {
    if (currentStepIndex === 1) {
      hasValidationErrors = !eligibilityValidatedAndCompleteRef.current
    } else if (isOnContactInfoStep) {
      const hasEmail = values?.contactInfo?.email?.trim()
      const address = values?.contactInfo?.address
      const hasAddress =
        address &&
        ((typeof address === 'string' && address.trim()) ||
          (typeof address === 'object' && (address.street || address.addressLine1 || address.address)))
      const hasRequiredFields =
        values?.contactInfo?.firstName &&
        values?.contactInfo?.lastName &&
        hasEmail &&
        values?.contactInfo?.phone &&
        hasAddress &&
        billHolderOk
      hasValidationErrors = !values?.contactInfo?.source || !hasRequiredFields
    } else {
      hasValidationErrors = errorCount > 0 && !isValidating
    }
  } else {
    if (currentStepIndex === 1) {
      hasValidationErrors = true
    } else if (isOnContactInfoStep) {
      hasValidationErrors =
        !values?.contactInfo?.source ||
        !values?.contactInfo?.firstName ||
        !values?.contactInfo?.lastName ||
        !values?.contactInfo?.email ||
        !values?.contactInfo?.phone ||
        !values?.contactInfo?.address ||
        !billHolderOk
    }
  }

  // ── HVAC Sales eligibility: block Next until all visible questions are answered ──
  const isHvacEligibilityStep = currentStepIndex === 1 && String(values?.path ?? '').trim().toLowerCase() === 'hvac'
  const isHvacEligibilityIncomplete = isHvacEligibilityStep && !isHvacEligibilityComplete(values)

  // ── HEA eligibility: block Next until all visible questions are answered ──
  const isHeaEligibilityStep = currentStepIndex === 1 && String(values?.path ?? '').trim().toLowerCase() === 'hea'
  const isHeaEligibilityIncomplete = isHeaEligibilityStep && !isHeaEligibilityComplete(values)

  // ── HealthyHome eligibility: same gate, but without gas/electric provider questions ──
  const isHealthyHomeEligibilityStep = currentStepIndex === 1 && String(values?.path ?? '').toLowerCase() === 'healthyhome'
  const isHealthyHomeEligibilityIncomplete = isHealthyHomeEligibilityStep && !isHealthyHomeEligibilityComplete(values)

  // ── Specific howHeated gate for HVAC (kept for legacy safety) ──
  const fuelAssistanceIsNo = values?.fuelAssistance === 'no'
  const familySizeVal = values?.familySize != null ? String(values.familySize).trim() : ''
  const showHowHeatedQuestion = fuelAssistanceIsNo && familySizeVal !== '' && ['1', '1-4'].includes(familySizeVal)
  const howHeatedNotSelected =
    values?.howHeated == null ||
    values?.howHeated === '' ||
    (typeof values?.howHeated === 'string' && values?.howHeated.trim() === '')
  const mustBlockNextStepForHowHeated = isHvacEligibilityStep && showHowHeatedQuestion && howHeatedNotSelected

  // ContactInfo becomes a submit step for HVAC Service, CAP HEA and paused-gas HEA
  // leads (all three skip Schedule)
  useEffect(() => {
    if (currentStepIndex === 2 && CurrentStep?.info) {
      const shouldBeSubmitStep =
        (path === 'hvac' && salesOrService === 'service') ||
        isCapHeaCustomer(values) ||
        isEversourceGasLead(values)
      CurrentStep.info.isSubmitStep = shouldBeSubmitStep
      setEnableSubmitButton(shouldBeSubmitStep)
    }
  }, [currentStepIndex, CurrentStep, path, salesOrService, values])

  const clearFollowingValuesHandler = useClearFollowingValues()

  // Re-validate whenever the schema or form values change so the Next button enables promptly
  useEffect(() => {
    if (validationSchema) {
      validateForm(validationSchema)
    }
  }, [currentStepIndex, validationSchema, values, validateForm])

  return (
    <>
      <FormEffect onChange={clearFollowingValuesHandler} />

      <form className="min-h-screen flex flex-col" onSubmit={handleSubmit}>
        <Navigation
          currentStepIndex={currentStepIndex}
          dataWasSent={dataWasSent}
          setCurrentStepIndex={setCurrentStepIndex}
          steps={steps}
        />

        <Main>
          {isIneligible && (
            <Modal
              initialOpen
              onClose={() => setIsIneligible(false)}
              title={isIneligible.when !== 'paymentAssistance' ? 'Sorry' : 'Give us a call'}
              body={() => <CallUsBody message={isIneligible.message} />}
              footer={(toggleModal) => <CallUsFooter toggleModal={toggleModal} />}
            />
          )}

          <CurrentStep
            currentStepIndex={currentStepIndex}
            setFieldValue={setFieldValue}
            setValidationSchema={setValidationSchema}
            values={values}
            setEnableSubmitButton={setEnableSubmitButton}
            setBlockNextStepUntilHowHeated={setBlockNextStepUntilHowHeated}
          />
        </Main>

        <Footer
          currentStep={CurrentStep}
          currentStepIndex={currentStepIndex}
          isSubmitting={isSubmitting}
          setCurrentStepIndex={setCurrentStepIndex}
          stepsLength={steps.length}
          enableSubmitButton={enableSubmitButton}
          values={values}
          hasErrors={
            hasValidationErrors ||
            mustBlockNextStepForHowHeated ||
            blockNextStepUntilHowHeated ||
            isHvacEligibilityIncomplete ||
            isHeaEligibilityIncomplete ||
            isHealthyHomeEligibilityIncomplete
          }
          isOnEligibilityStep={hasPath && hasEligibilityAnswers && !hasContactInfo}
        />
        <ErrorFocus isSubmitting={isSubmitting} isValidating={isValidating} errors={errors} />
      </form>

      <FixedHelpButton />
    </>
  )
}

/**
 * True when the current URL is a HealthyHome entry point.
 * Accepts both the current `/HealthyHome` path and the legacy `/HomeCare` path
 * (renamed to HealthyHome) so old links, bookmarks, and QR codes keep working.
 */
const isHealthyHomeEntry = () => {
  const pathname = window.location.pathname.toLowerCase()
  return pathname.startsWith('/healthyhome') || pathname.startsWith('/homecare')
}

/**
 * Rewrites the address bar to /HealthyHome when the user arrives via the legacy
 * /HomeCare path, preserving any query string. Uses replaceState so the Back button
 * does not return to the old URL.
 */
const redirectLegacyHomeCarePath = () => {
  if (window.location.pathname.toLowerCase().startsWith('/homecare')) {
    history.replaceState(null, null, `/HealthyHome${window.location.search}`)
  }
}

/**
 * Returns the initial step index based on the current URL.
 * /HealthyHome* (and legacy /HomeCare*) → step 1 (Eligibility), bypassing ChoosePath.
 * All other paths → step 0 (ChoosePath).
 */
const getInitialStep = () => (isHealthyHomeEntry() ? 1 : 0)

/**
 * Returns the initial form values.
 * Pre-sets `path = 'healthyhome'` when the user navigates directly to /HealthyHome
 * (or the legacy /HomeCare), so Eligibility.jsx renders the HealthyHome variant
 * without requiring ChoosePath.
 */
const getInitialValues = () => {
  const base = {
    path: '',
    salesOrService: '',
    contactInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      languagePref: 'English',
      secondaryContact: false,
      nameOnBill: '',
      textOutIn: false,
      dataSharingAuthorized: false,
    },
  }
  if (isHealthyHomeEntry()) {
    base.path = 'healthyhome'
  }
  return base
}

/**
 * Root App component.
 * Wraps AppContent with FormProvider so every child can read and write form state.
 * Reads the URL on mount to determine the starting step and initial path value.
 */
const App = () => {
  // Rewrite legacy /HomeCare URLs to /HealthyHome before reading initial state.
  redirectLegacyHomeCarePath()
  return (
    <FormProvider initialValues={getInitialValues()}>
      <AppContent initialStep={getInitialStep()} />
    </FormProvider>
  )
}

export default App
